import type {
  ShiftEditorDraft,
  ShiftEditorDraftVehicleSnapshot,
} from '@der-maschinfuehrer/contracts';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { OptionalZonedMomentField } from '../../components/OptionalZonedMomentField';
import { TextField } from '../../components/form-primitives';
import { updateDraftDistanceMetres } from '../../domain/alpha-shift';
import { formatMetresAsKilometres, parseKilometresToMetres } from '../../domain/zoned-moment';

const WORK_TYPE_OPTIONS = [
  ['passenger', 'Osobní doprava'],
  ['freight', 'Nákladní doprava'],
  ['shunting', 'Posun'],
  ['light_engine', 'Samostatná jízda hnacího vozidla'],
  ['service_run', 'Služební jízda'],
  ['other', 'Jiný výkon'],
] as const;

const ROLE_OPTIONS = [
  ['driver', 'Strojvedoucí'],
  ['second_driver', 'Druhý strojvedoucí'],
  ['instructor', 'Instruktor'],
  ['transport', 'Přeprava'],
  ['other', 'Jiná role'],
] as const;

const VEHICLE_TYPE_OPTIONS = [
  ['locomotive', 'Lokomotiva'],
  ['multiple_unit', 'Jednotka'],
  ['driving_trailer', 'Řídicí vůz'],
  ['railcar', 'Motorový vůz'],
  ['other', 'Jiné vozidlo'],
] as const;

const STATUS_OPTIONS = [
  ['draft', 'Rozpracovaný'],
  ['complete', 'Dokončený'],
  ['cancelled', 'Zrušený'],
  ['partial', 'Předčasně ukončený'],
] as const;

export interface SegmentAdditionalFieldsProps {
  readonly disabled?: boolean;
  readonly draft: ShiftEditorDraft;
  readonly expandedNonPerformanceUnavailable?: boolean;
  readonly onChange: (nextDraft: ShiftEditorDraft) => void;
  readonly plannedDestinationField?: ReactNode;
  readonly warnings?: readonly string[];
}

interface RawFieldState {
  readonly distanceError: string | null;
  readonly distanceInput: string;
  readonly distanceModeSource: ShiftEditorDraft['distance_input_mode'];
  readonly distanceSource: number | null;
  readonly sourceTripId: string;
  readonly tagsError: string | null;
  readonly tagsInput: string;
  readonly tagsSource: string;
}

export function SegmentAdditionalFields({
  disabled = false,
  draft,
  expandedNonPerformanceUnavailable = false,
  onChange,
  plannedDestinationField,
  warnings = [],
}: SegmentAdditionalFieldsProps) {
  const generatedId = useId();
  const distanceElement = useRef<HTMLInputElement>(null);
  const tagsElement = useRef<HTMLInputElement>(null);
  const currentTagsSource = tagsSource(draft.tags);
  const [storedRawFields, setRawFields] = useState<RawFieldState>(() => ({
    distanceError: null,
    distanceInput: formatMetresAsKilometres(draft.distance_metres),
    distanceModeSource: draft.distance_input_mode,
    distanceSource: draft.distance_metres,
    sourceTripId: draft.trip_segment_id,
    tagsError: null,
    tagsInput: draft.tags.join(', '),
    tagsSource: currentTagsSource,
  }));
  let rawFields = storedRawFields;
  const distanceIsCurrent =
    rawFields.sourceTripId === draft.trip_segment_id &&
    rawFields.distanceModeSource === draft.distance_input_mode &&
    rawFields.distanceSource === draft.distance_metres;
  const tagsAreCurrent =
    rawFields.sourceTripId === draft.trip_segment_id && rawFields.tagsSource === currentTagsSource;
  if (!distanceIsCurrent || !tagsAreCurrent) {
    rawFields = {
      distanceError: distanceIsCurrent ? rawFields.distanceError : null,
      distanceInput: distanceIsCurrent
        ? rawFields.distanceInput
        : formatMetresAsKilometres(draft.distance_metres),
      distanceModeSource: draft.distance_input_mode,
      distanceSource: draft.distance_metres,
      sourceTripId: draft.trip_segment_id,
      tagsError: tagsAreCurrent ? rawFields.tagsError : null,
      tagsInput: tagsAreCurrent ? rawFields.tagsInput : draft.tags.join(', '),
      tagsSource: currentTagsSource,
    };
  }

  useEffect(() => {
    distanceElement.current?.setCustomValidity(rawFields.distanceError ?? '');
  }, [rawFields.distanceError]);

  useEffect(() => {
    tagsElement.current?.setCustomValidity(rawFields.tagsError ?? '');
  }, [rawFields.tagsError]);

  const patchDraft = (patch: Partial<ShiftEditorDraft>) => {
    onChange({ ...draft, ...patch });
  };

  const updateDistance = (value: string) => {
    try {
      const distanceMetres = parseKilometresToMetres(value);
      const nextDraft = updateDraftDistanceMetres(draft, distanceMetres);
      setRawFields({
        ...rawFields,
        distanceError: null,
        distanceInput: value,
        distanceModeSource: nextDraft.distance_input_mode,
        distanceSource: distanceMetres,
      });
      if (nextDraft !== draft) onChange(nextDraft);
    } catch (error) {
      setRawFields({
        ...rawFields,
        distanceError: error instanceof Error ? error.message : 'Kilometráž nemá platný formát.',
        distanceInput: value,
        distanceModeSource: draft.distance_input_mode,
        distanceSource: draft.distance_metres,
      });
    }
  };

  const updateTags = (value: string) => {
    const tags = value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag !== '');
    if (tags.some((tag) => tag.length > 64)) {
      setRawFields({
        ...rawFields,
        tagsError: 'Každý štítek může mít nejvýše 64 znaků.',
        tagsInput: value,
        tagsSource: currentTagsSource,
      });
      return;
    }
    if (tags.length > 64) {
      setRawFields({
        ...rawFields,
        tagsError: 'K úseku lze uložit nejvýše 64 štítků.',
        tagsInput: value,
        tagsSource: currentTagsSource,
      });
      return;
    }
    if (new Set(tags).size !== tags.length) {
      setRawFields({
        ...rawFields,
        tagsError: 'Každý štítek uveď pouze jednou.',
        tagsInput: value,
        tagsSource: currentTagsSource,
      });
      return;
    }
    setRawFields({
      ...rawFields,
      tagsError: null,
      tagsInput: value,
      tagsSource: tagsSource(tags),
    });
    patchDraft({ tags });
  };

  const updateVehicle = (
    vehicleId: string,
    patch: Partial<Omit<ShiftEditorDraftVehicleSnapshot, 'vehicle_id'>>,
  ) => {
    patchDraft({
      vehicles: draft.vehicles.map((vehicle) =>
        vehicle.vehicle_id === vehicleId ? { ...vehicle, ...patch } : vehicle,
      ),
    });
  };

  const addVehicle = () => {
    if (disabled || draft.vehicles.length >= 16) return;
    patchDraft({
      vehicles: [
        ...draft.vehicles,
        {
          vehicle_id: crypto.randomUUID(),
          series: '',
          full_number: '',
          nickname: '',
          operator: '',
          vehicle_type: null,
        },
      ],
    });
  };

  const removeVehicle = (vehicleId: string) => {
    patchDraft({
      vehicles: draft.vehicles.filter((vehicle) => vehicle.vehicle_id !== vehicleId),
    });
  };

  return (
    <section className="segment-additional-fields" aria-labelledby={`${generatedId}-heading`}>
      <h3 id={`${generatedId}-heading`}>Údaje úseku</h3>
      <p>Skutečné časy jsou volitelné a bez výslovného zadání zůstanou neznámé.</p>

      {warnings.length > 0 ? (
        <div className="error-summary" role="status" aria-live="polite">
          <strong>Upozornění k úseku</strong>
          <ul>
            {warnings.map((warning, index) => (
              <li key={`${String(index)}-${warning}`}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <OptionalZonedMomentField
        key={`segment-start-${draft.trip_segment_id}`}
        disabled={disabled}
        label="Skutečný začátek úseku"
        name="segment-actual-start"
        value={draft.actual_start}
        onChange={(actualStart) => patchDraft({ actual_start: actualStart })}
      />
      <OptionalZonedMomentField
        key={`segment-end-${draft.trip_segment_id}`}
        disabled={disabled}
        label="Skutečný konec úseku"
        name="segment-actual-end"
        value={draft.actual_end}
        onChange={(actualEnd) => patchDraft({ actual_end: actualEnd })}
      />

      <TextField
        disabled={disabled}
        label="Číslo vlaku nebo výkonu"
        maxLength={128}
        name="segment-train-or-service-number"
        value={draft.train_or_service_number}
        onChange={(event) => patchDraft({ train_or_service_number: event.currentTarget.value })}
      />

      <label className="form-field">
        <span>Druh výkonu</span>
        <select
          className="text-input touch-target"
          disabled={disabled}
          name="segment-work-type"
          value={draft.work_type ?? ''}
          onChange={(event) =>
            patchDraft({
              work_type: nullableSelectValue<NonNullable<ShiftEditorDraft['work_type']>>(
                event.currentTarget.value,
              ),
            })
          }
        >
          <option value="">Neuvedeno</option>
          {WORK_TYPE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="form-field">
        <span>Role</span>
        <select
          className="text-input touch-target"
          disabled={disabled}
          name="segment-role"
          value={draft.role ?? ''}
          onChange={(event) =>
            patchDraft({
              role: nullableSelectValue<NonNullable<ShiftEditorDraft['role']>>(
                event.currentTarget.value,
              ),
            })
          }
        >
          <option value="">Neuvedeno</option>
          {ROLE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <TextField
        disabled={disabled}
        label="Dopravce nebo provozovatel"
        maxLength={256}
        name="segment-operator"
        value={draft.operator}
        onChange={(event) => patchDraft({ operator: event.currentTarget.value })}
      />

      <fieldset className="form-field">
        <legend>Vozidla</legend>
        <p className="field-description">Lze uvést až 16 vozidel v pořadí na úseku.</p>
        {draft.vehicles.length === 0 ? <p>Zatím není uvedeno žádné vozidlo.</p> : null}
        {draft.vehicles.map((vehicle, index) => (
          <fieldset className="form-field" key={vehicle.vehicle_id}>
            <legend>Vozidlo {index + 1}</legend>
            <TextField
              disabled={disabled}
              label="Řada"
              maxLength={512}
              name={`vehicle-${vehicle.vehicle_id}-series`}
              value={vehicle.series}
              onChange={(event) =>
                updateVehicle(vehicle.vehicle_id, { series: event.currentTarget.value })
              }
            />
            <TextField
              disabled={disabled}
              label="Úplné číslo"
              maxLength={64}
              name={`vehicle-${vehicle.vehicle_id}-full-number`}
              value={vehicle.full_number}
              onChange={(event) =>
                updateVehicle(vehicle.vehicle_id, { full_number: event.currentTarget.value })
              }
            />
            <TextField
              disabled={disabled}
              label="Přezdívka"
              maxLength={128}
              name={`vehicle-${vehicle.vehicle_id}-nickname`}
              value={vehicle.nickname}
              onChange={(event) =>
                updateVehicle(vehicle.vehicle_id, { nickname: event.currentTarget.value })
              }
            />
            <TextField
              disabled={disabled}
              label="Dopravce nebo provozovatel vozidla"
              maxLength={256}
              name={`vehicle-${vehicle.vehicle_id}-operator`}
              value={vehicle.operator}
              onChange={(event) =>
                updateVehicle(vehicle.vehicle_id, { operator: event.currentTarget.value })
              }
            />
            <label className="form-field">
              <span>Typ vozidla</span>
              <select
                className="text-input touch-target"
                disabled={disabled}
                name={`vehicle-${vehicle.vehicle_id}-type`}
                value={vehicle.vehicle_type ?? ''}
                onChange={(event) =>
                  updateVehicle(vehicle.vehicle_id, {
                    vehicle_type: nullableSelectValue<
                      NonNullable<ShiftEditorDraftVehicleSnapshot['vehicle_type']>
                    >(event.currentTarget.value),
                  })
                }
              >
                <option value="">Neuvedeno</option>
                {VEHICLE_TYPE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="secondary-action touch-target"
              disabled={disabled}
              type="button"
              onClick={() => removeVehicle(vehicle.vehicle_id)}
            >
              Odebrat vozidlo {index + 1}
            </button>
          </fieldset>
        ))}
        <button
          className="secondary-action touch-target"
          disabled={disabled || draft.vehicles.length >= 16}
          type="button"
          onClick={addVehicle}
        >
          {draft.vehicles.length >= 16 ? 'Limit 16 vozidel dosažen' : 'Přidat vozidlo'}
        </button>
      </fieldset>

      <TextField
        ref={distanceElement}
        disabled={disabled}
        {...(rawFields.distanceError === null ? {} : { error: rawFields.distanceError })}
        inputMode="decimal"
        label="Ruční kilometráž (km)"
        name="segment-distance-kilometres"
        placeholder="např. 124,375"
        value={rawFields.distanceInput}
        onChange={(event) => updateDistance(event.currentTarget.value)}
      />

      <label className="form-field">
        <span>Stav úseku</span>
        <select
          className="text-input touch-target"
          disabled={disabled}
          name="segment-status"
          value={draft.status}
          onChange={(event) => {
            const status = event.currentTarget.value as ShiftEditorDraft['status'];
            patchDraft(
              status === 'partial'
                ? { status }
                : { status, planned_destination: null, early_termination_reason: '' },
            );
          }}
        >
          {STATUS_OPTIONS.map(([value, label]) => (
            <option
              key={value}
              value={value}
              disabled={
                expandedNonPerformanceUnavailable && (value === 'draft' || value === 'cancelled')
              }
            >
              {label}
            </option>
          ))}
        </select>
      </label>

      {expandedNonPerformanceUnavailable ? (
        <p className="field-description" role="status">
          Rozpracovaný nebo zrušený stav s rozepsaným místem, vybranou trasou, body přes či
          kilometráží teď nelze bezpečně uložit. Dokončenou a předčasně ukončenou jízdu uložit lze.
        </p>
      ) : null}

      {draft.status === 'partial' ? (
        <fieldset className="form-field">
          <legend>Předčasné ukončení</legend>
          {plannedDestinationField ?? (
            <p className="field-description">Původně plánovaný cíl zatím není zadaný.</p>
          )}
          <TextField
            disabled={disabled}
            label="Důvod předčasného ukončení"
            maxLength={1_000}
            name="segment-early-termination-reason"
            required
            value={draft.early_termination_reason}
            onChange={(event) =>
              patchDraft({ early_termination_reason: event.currentTarget.value })
            }
          />
        </fieldset>
      ) : null}

      <label className="form-field">
        <span>Poznámka k úseku</span>
        <textarea
          className="text-input"
          disabled={disabled}
          maxLength={10_000}
          name="segment-note"
          value={draft.note}
          onChange={(event) => patchDraft({ note: event.currentTarget.value })}
        />
      </label>

      <TextField
        ref={tagsElement}
        disabled={disabled}
        description="Více štítků odděl čárkou. Pořadí zůstane zachované."
        {...(rawFields.tagsError === null ? {} : { error: rawFields.tagsError })}
        label="Štítky"
        name="segment-tags"
        value={rawFields.tagsInput}
        onChange={(event) => updateTags(event.currentTarget.value)}
      />
    </section>
  );
}

function nullableSelectValue<Value extends string>(value: string): Value | null {
  return value === '' ? null : (value as Value);
}

function tagsSource(tags: readonly string[]): string {
  return tags.join('\u0000');
}
